import React, { useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import './ConversorGNDI.css';

const ConversorGNDI = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [formatType, setFormatType] = useState('padrao'); // 'padrao' ou 'sixvox'

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
      setSuccess(false);
      setPreviewData(null);
    }
  };

  const processFile = async (format) => {
    if (!file) {
      setError('Por favor, selecione um arquivo para converter.');
      return;
    }

    setFormatType(format);
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Ler o arquivo como texto
      const fileContent = await readFileAsText(file);
      
      // Processar o CSV com PapaParse
      Papa.parse(fileContent, {
        header: true,
        delimiter: ';',
        encoding: 'CP1252',
        skipEmptyLines: true,
        dynamicTyping: false, // Mantemos como string para preservar formatação
        complete: function(results) {
          if (results.errors.length > 0) {
            setError(`Erro ao processar o CSV: ${results.errors[0].message}`);
            setLoading(false);
            return;
          }

          // Mapeamento dinâmico dos headers
          const headers = Object.keys(results.data[0] || {});
          const headerMap = buildHeaderMap(headers);

          // LOGS DE DEPURAÇÃO
          console.log('Headers lidos do CSV:', headers);
          console.log('HeaderMap gerado:', headerMap);
          console.log('Primeira linha de dados:', results.data[0]);
          // FIM LOGS
          
          // Transformar os dados conforme a lógica solicitada
          let transformedData;
          
          if (format === 'sixvox') {
            transformedData = transformDataSixvox(results.data, headerMap);
          } else {
            transformedData = transformData(results.data, headerMap);
          }
          
          // Definir uma prévia dos dados
          setPreviewData(transformedData.slice(0, 5));
          
          // Gerar o arquivo Excel
          generateExcel(transformedData, format);
          
          setSuccess(true);
          setLoading(false);
        },
        error: function(error) {
          setError(`Erro ao processar o arquivo: ${error.message}`);
          setLoading(false);
        }
      });
    } catch (err) {
      setError(`Erro ao ler o arquivo: ${err.message}`);
      setLoading(false);
    }
  };

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e.target.error);
      reader.readAsText(file, 'CP1252'); // Encoding Windows-1252
    });
  };

  // Adicionar função utilitária para mapear headers duplicados
  const buildHeaderMap = (headers) => {
    // Lista dos campos esperados (pode ser expandida conforme necessário)
    const expected = [
      'VENCIMENTO', 'CD ORIGEM', 'CD EMPRESA', 'EMPRESA', 'VL COMISSÃO', 'PARCELA',
      'VL.LIQUIDO', 'VL BASE ODONTO', 'VL.BASE ODONTO', 'VL.LIQUIDO', 'VL COMISSAO', 'VL COMISSÃO'
    ];
    const map = {};
    expected.forEach((campo) => {
      // Procura o header exato ou com sufixo _1, _2, etc
      const found = headers.find(h => h === campo) || headers.find(h => h.startsWith(campo + '_'));
      if (found) map[campo] = found;
    });
    return map;
  };

  // Adaptar as funções de transformação para usar o headerMap
  const transformData = (data, headerMap) => {
    const grupos = {};
    let linhasProcessadas = 0;
    data.forEach(row => {
      // Ignorar linhas sem dados essenciais (apenas VENCIMENTO e CD EMPRESA agora)
      if (!row[headerMap['VENCIMENTO']] || !row[headerMap['CD EMPRESA']]) return;
      linhasProcessadas++;
      const chave = `${row[headerMap['VENCIMENTO']]}-${row[headerMap['CD ORIGEM']] || ''}-${row[headerMap['CD EMPRESA']]}-${row[headerMap['EMPRESA']]}-${row[headerMap['VL COMISSÃO']]}`;
      if (!grupos[chave]) {
        grupos[chave] = {
          vencimento: row[headerMap['VENCIMENTO']],
          cdOrigem: row[headerMap['CD ORIGEM']] || '',
          cdEmpresa: row[headerMap['CD EMPRESA']],
          empresa: row[headerMap['EMPRESA']],
          vlComissao: row[headerMap['VL COMISSÃO']],
          parcelas: [],
          vlLiquidoTotal: 0,
          vlBaseOdontoTotal: 0
        };
      }
      if (row[headerMap['PARCELA']]) {
        grupos[chave].parcelas.push(parseInt(row[headerMap['PARCELA']]) || 0);
      }
      const vlLiquido = parseFloat((row[headerMap['VL.LIQUIDO']] || '0').replace('.', '').replace(',', '.')) || 0;
      grupos[chave].vlLiquidoTotal += vlLiquido;
      const baseOdontoHeader = headerMap['VL BASE ODONTO'] || headerMap['VL.BASE ODONTO'];
      const vlBaseOdonto = parseFloat((row[baseOdontoHeader] || '0').replace('.', '').replace(',', '.')) || 0;
      grupos[chave].vlBaseOdontoTotal += vlBaseOdonto;
    });
    const resultado = Object.values(grupos).map(grupo => {
      const maiorParcela = grupo.parcelas.length > 0 ? Math.max(...grupo.parcelas) : 0;
      const baseCalculo = grupo.vlLiquidoTotal + grupo.vlBaseOdontoTotal;
      return {
        'VENCIMENTO': grupo.vencimento,
        'CD ORIGEM': grupo.cdOrigem,
        'CD EMPRESA': grupo.cdEmpresa,
        'EMPRESA': grupo.empresa,
        'VL COMISSÃO': grupo.vlComissao,
        'PARCELA': maiorParcela,
        'BASE DE CALCULO': baseCalculo.toFixed(2).replace('.', ',')
      };
    });
    // Aviso se nenhuma linha foi processada
    if (linhasProcessadas === 0) {
      alert('Nenhuma linha foi processada. Verifique se os campos VENCIMENTO e CD EMPRESA estão preenchidos no arquivo.');
    }
    return resultado;
  };
  
  const transformDataSixvox = (data, headerMap) => {
    const hoje = new Date();
    const dataAtual = `${String(hoje.getDate()).padStart(2, '0')}/${String(hoje.getMonth() + 1).padStart(2, '0')}/${String(hoje.getFullYear()).slice(-2)}`;
    const grupos = {};
    let linhasProcessadas = 0;
    data.forEach(row => {
      if (!row[headerMap['VENCIMENTO']] || !row[headerMap['CD EMPRESA']]) return;
      linhasProcessadas++;
      const chave = `${row[headerMap['VENCIMENTO']]}-${row[headerMap['CD ORIGEM']] || ''}-${row[headerMap['CD EMPRESA']]}-${row[headerMap['EMPRESA']]}-${row[headerMap['VL COMISSÃO']]}`;
      if (!grupos[chave]) {
        grupos[chave] = {
          vencimento: row[headerMap['VENCIMENTO']],
          cdOrigem: row[headerMap['CD ORIGEM']] || '',
          cdEmpresa: row[headerMap['CD EMPRESA']],
          empresa: row[headerMap['EMPRESA']],
          vlComissao: row[headerMap['VL COMISSÃO']],
          parcelas: [],
          vlLiquidoTotal: 0,
          vlBaseOdontoTotal: 0
        };
      }
      if (row[headerMap['PARCELA']]) {
        grupos[chave].parcelas.push(parseInt(row[headerMap['PARCELA']]) || 0);
      }
      const vlLiquido = parseFloat((row[headerMap['VL.LIQUIDO']] || '0').replace('.', '').replace(',', '.')) || 0;
      grupos[chave].vlLiquidoTotal += vlLiquido;
      const baseOdontoHeader = headerMap['VL BASE ODONTO'] || headerMap['VL.BASE ODONTO'];
      const vlBaseOdonto = parseFloat((row[baseOdontoHeader] || '0').replace('.', '').replace(',', '.')) || 0;
      grupos[chave].vlBaseOdontoTotal += vlBaseOdonto;
    });
    const resultado = Object.values(grupos).map(grupo => {
      const maiorParcela = grupo.parcelas.length > 0 ? Math.max(...grupo.parcelas) : 0;
      const baseCalculo = grupo.vlLiquidoTotal + grupo.vlBaseOdontoTotal;
      const comissao = parseFloat(grupo.vlComissao.replace(',', '.'));
      return {
        'Data_atual': dataAtual,
        'Administradora': '',
        'Operadora': 'HAPVIDA NOTREDAME (169)',
        'Tipo': 'pj',
        'Modalidade': 'PME SAÚDE - ATÉ 29 VIDAS (163)',
        'Proposta': grupo.cdOrigem,
        'Nome': grupo.empresa,
        'Saude/Dental': 's',
        'parcela': maiorParcela,
        'Base Calculo': baseCalculo,
        'Base Calculo_2': baseCalculo, 
        'Comissão': comissao,
        'Recibo': 10
      };
    });
    if (linhasProcessadas === 0) {
      alert('Nenhuma linha foi processada. Verifique se os campos VENCIMENTO e CD EMPRESA estão preenchidos no arquivo.');
    }
    return resultado;
  };

  const generateExcel = (data, format) => {
    try {
      // Criar uma planilha
      const worksheet = XLSX.utils.json_to_sheet(data);
      
      // Se for formato Sixvox, ajustar os tipos de células
      if (format === 'sixvox') {
        // Obter o range da planilha
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        
        // Para cada linha de dados (pulando o cabeçalho)
        for (let R = range.s.r + 1; R <= range.e.r; R++) {
          // Configurar a coluna de data como tipo data
          const dataCell = XLSX.utils.encode_cell({r: R, c: 0});
          if (worksheet[dataCell]) {
            worksheet[dataCell].t = 's';  // Tipo string
            worksheet[dataCell].v = data[R - 1]['Data_atual'];  // Valor como string formatada
          }
          
          // Configurar as colunas numéricas
          ['Proposta', 'parcela', 'Base Calculo', 'Base Calculo_2', 'Comissão', 'Recibo'].forEach((col, idx) => {
            // Índices das colunas (ajuste conforme necessário)
            const indices = {
              'Proposta': 5,
              'parcela': 8,
              'Base Calculo': 9,
              'Base Calculo_2': 10,
              'Comissão': 11,
              'Recibo': 12
            };
            
            const cell = XLSX.utils.encode_cell({r: R, c: indices[col]});
            if (worksheet[cell]) {
              worksheet[cell].t = 'n';  // Tipo numérico
            }
          });
        }
      }
      
      // Criar um livro
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados Formatados');
      
      // Gerar o arquivo
      const excelBuffer = XLSX.write(workbook, { bookType: 'xls', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.ms-excel' });
      
      // Criar link para download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Nome do arquivo baseado no formato
      const fileName = format === 'sixvox' ? 'GNDI_Sixvox.xls' : 'GNDI_Formatado.xls';
      link.download = fileName;
      
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 0);
    } catch (err) {
      setError(`Erro ao gerar o arquivo Excel: ${err.message}`);
    }
  };

  return (
    <div className="container">
      <h1>Conversor GNDI para Excel</h1>
      
      <div className="upload-section">
        <div className="file-input">
          <label>
            Selecione o arquivo CSV:
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
            />
          </label>
        </div>
        
        <div className="button-group">
          <button
            onClick={() => processFile('padrao')}
            disabled={loading || !file}
            className={loading || !file ? 'button-disabled' : 'button'}
          >
            {loading && formatType === 'padrao' ? 'Processando...' : 'Converter para Excel'}
          </button>
          
          <button
            onClick={() => processFile('sixvox')}
            disabled={loading || !file}
            className={loading || !file ? 'button-disabled' : 'button sixvox-button'}
          >
            {loading && formatType === 'sixvox' ? 'Processando...' : 'Converter Excel Sixvox'}
          </button>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {success && (
        <div className="success-message">
          Arquivo processado com sucesso! O download começará automaticamente.
        </div>
      )}
      
      {previewData && previewData.length > 0 && (
        <div className="preview-section">
          <h2>Prévia dos dados convertidos ({formatType === 'sixvox' ? 'Formato Sixvox' : 'Formato Padrão'}):</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {Object.keys(previewData[0]).map((key, index) => (
                    <th key={index}>
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {Object.values(row).map((value, valueIndex) => (
                      <td key={valueIndex}>
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="info-section">
        <h3>Sobre o Processamento:</h3>
        
        <div className="tabs">
          <div className={`tab ${formatType === 'padrao' ? 'active' : ''}`} onClick={() => setFormatType('padrao')}>
            Formato Padrão
          </div>
          <div className={`tab ${formatType === 'sixvox' ? 'active' : ''}`} onClick={() => setFormatType('sixvox')}>
            Formato Sixvox
          </div>
        </div>
        
        {formatType === 'padrao' ? (
          <ul>
            <li>O sistema agrupa os dados por VENCIMENTO, CD ORIGEM, CD EMPRESA, EMPRESA e VL COMISSÃO</li>
            <li>Para cada grupo, é selecionada a maior PARCELA</li>
            <li>A BASE DE CALCULO é calculada somando VL.LIQUIDO + VL.BASE ODONTO</li>
            <li>O arquivo Excel é gerado com apenas estas colunas relevantes</li>
          </ul>
        ) : (
          <ul>
            <li>O sistema agrupa os dados da mesma forma que o formato padrão</li>
            <li>O formato Sixvox inclui colunas específicas para importação no sistema Sixvox</li>
            <li>Dados adicionados: Data atual, Operadora, Tipo, Modalidade, etc.</li>
            <li>A base de cálculo e a comissão são formatadas com vírgula como separador decimal</li>
          </ul>
        )}
      </div>
    </div>
  );
};

export default ConversorGNDI;